import "../../../assets/scss/google-cloud-docker-file-list-style.scss";
import { useEffect, useState } from "react";

import { useTranslation } from "react-i18next";
import { Dropdown, MenuProps, message } from "antd";
import {
  CloudDownloadOutlined,
  DeleteOutlined,
  MoreOutlined,
} from "@ant-design/icons";
import {
  getDockerFileList,
  uploadDockerToGoogleDrive,
} from "../../server/GoogleDrive";
import { useAppDispatch, useAppSelector } from "../../store/hooks";

import {
  updateCurrDockerFolderName,
  updateDockerList,
  updateFolderPath,
} from "../../features/cloud/cloudSlice";
import { getGoogleDriveState } from "../../utility/common/session-store";
import LoadingModal from "../LoadingModal";

const DockerItem = (props: any) => {
  const { t } = useTranslation();
  const { index, file, disabled } = props;
  const [newFileName, setNewFileName] = useState<string>("");
  const [entering, setEntering] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [messageApi, contextHolder] = message.useMessage();

  const userState = useAppSelector((state: any) => state.user);
  const selectedDocker = useAppSelector(
    (state: any) => state.cloud.selectedDocker,
  );
  const folderPath = useAppSelector((state: any) => state.cloud.folderPath);
  const dispatch = useAppDispatch();

  const uploadToGoogleDrive = (origFile: string, newFileName: string) => {
    downloadLoading();
    uploadDockerToGoogleDrive({
      drive_file_name: newFileName,
      source_path: `./${folderPath}${origFile}`,
      instance_id: selectedDocker.instance_id,
      host_id: selectedDocker.host_id,
      user_id: userState.user_id,
    })
      .then((res) => {
        if (res.data.code === 0) {
          downloadSuccess();
        }
      })
      .catch((err) => {
        downloadError();
        if (getGoogleDriveState() == "") {
          stateError();
        }
      });
  };

  const deleteItem = async (name: string, isFolder: boolean) => {
    setDeleting(true);
    const currentPath =
      !folderPath || folderPath === "/" ? "" : folderPath.replace(/\/+$/, "");

    // Build payload depending on whether we delete a folder or file
    const payload = isFolder
      ? {
          command: 6, // delete folder
          new_file_name: "",
          folder: `${currentPath}/${name}`, // full folder path to delete
          search_keyword: "",
          file: "",
          instance_id: selectedDocker.instance_id,
          host_id: selectedDocker.host_id,
          user_id: userState.user_id,
        }
      : {
          command: 7, // delete file
          new_file_name: "",
          folder: currentPath, // current directory
          search_keyword: "",
          file: name, // file name only
          instance_id: selectedDocker.instance_id,
          host_id: selectedDocker.host_id,
          user_id: userState.user_id,
        };

    try {
      // 1️⃣ Step 1: Perform deletion
      await getDockerFileList(payload);

      // 2️⃣ Step 2: Refresh current folder file list
      const refreshRes = await getDockerFileList({
        command: 9, // fetch current folder contents
        new_file_name: "",
        folder: currentPath,
        search_keyword: "",
        file: "",
        instance_id: selectedDocker.instance_id,
        host_id: selectedDocker.host_id,
        user_id: userState.user_id,
      });

      // 3️⃣ Step 3: Update Redux state & UI
      dispatch(updateDockerList(refreshRes.data.ops_result));
      deleteSuccess();
    } catch (err) {
      console.error("deleteItem error:", err);
      deleteError();
      if (getGoogleDriveState() === "") stateError();
    } finally {
      setDeleting(false);
    }
  };

  const enterFolder = (name: string) => {
    if (disabled || entering) return;
    // Normalize folderPath
    const basePath =
      folderPath === "" || folderPath === "/"
        ? ""
        : folderPath.replace(/\/+$/, ""); // remove trailing slashes

    const nextFolder = `${basePath}/${name}`;

    setEntering(true);
    getDockerFileList({
      command: 2,
      new_file_name: "",
      folder: nextFolder,
      search_keyword: "",
      file: "",
      instance_id: selectedDocker.instance_id,
      host_id: selectedDocker.host_id,
      user_id: userState.user_id,
    })
      .then((res) => {
        const treatedPath = `${nextFolder}/`;
        dispatch(updateDockerList(res.data.ops_result));
        dispatch(updateFolderPath(treatedPath));
        dispatch(updateCurrDockerFolderName(name));
      })
      .catch((err) => {
        console.log("folderPath", folderPath);
        enterError();
        dispatch(updateCurrDockerFolderName(""));
        dispatch(updateFolderPath(""));
      })
      .finally(() => {
        setEntering(false);
      });
  };

  const treatSize = (size: number) => {
    if (isNaN(size) || size <= 0) return "-";

    if (size < 1024) return `${size} B`;
    if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
    if (size < 1024 * 1024 * 1024)
      return `${(size / 1024 / 1024).toFixed(1)} MB`;
    return `${(size / 1024 / 1024 / 1024).toFixed(1)} GB`;
  };

  const formatDate = (raw: string) => {
    // "十 30 18:29" → treat month as text, leave unchanged if localized
    return raw.replace(/\s+/g, " ");
  };

  const isFolder = file.permissions.startsWith("d");

  const downloadLoading = () => {
    messageApi.loading(t("Downloading..."), 1.5);
  };

  const downloadSuccess = () => {
    messageApi
      .loading(t("Download Success!"), 1.5)
      .then(() => window.location.reload());
  };

  const downloadError = () => {
    messageApi.error(t("Download Failed!"));
  };

  const deleteSuccess = () => {
    messageApi.success(t("Delete Success!"), 1.5);
  };

  const deleteError = () => {
    messageApi.error(t("Delete Failed!"));
  };

  const enterError = () => {
    messageApi.error(t("Loading Failed!"));
  };

  const stateError = () => {
    messageApi.error(t("Please connect to Google Drive!"));
  };

  useEffect(() => {
    if (folderPath !== "" && !folderPath.includes("/")) {
      dispatch(updateFolderPath(""));
    }
  }, []);

  const items: MenuProps["items"] = [
    {
      key: "1",
      label: (
        <span onClick={() => uploadToGoogleDrive(file.name, newFileName)}>
          <CloudDownloadOutlined /> &nbsp;
          {t("Download to Google Drive")}
        </span>
      ),
    },
    {
      key: "2",
      label: (
        <span onClick={() => deleteItem(file.name, false)}>
          <DeleteOutlined /> &nbsp;
          {t("Delete")}
        </span>
      ),
    },
  ];
  const itemsFolder: MenuProps["items"] = [
    {
      key: "0",
      label: (
        <span onClick={() => deleteItem(file.name, true)}>
          <DeleteOutlined /> &nbsp;
          {t("Delete Folder")}
        </span>
      ),
    },
  ];

  return (
    <>
      {contextHolder}
      {(entering || deleting) && <LoadingModal />}
      <div
        key={index}
        className="row file-list-item"
        style={{
          position: "relative",
          cursor: "pointer",
        }}
      >
        <span
          className="column"
          onClick={() => !disabled && isFolder && enterFolder(file.name)}
          style={{ pointerEvents: disabled ? "none" : "auto" }}
        >
          <img
            width="20"
            style={{ marginRight: ".5em" }}
            src={
              isFolder
                ? "./images/GoogleDriveBtn/folder.png"
                : "./images/GoogleDriveBtn/file.png"
            }
            alt={isFolder ? "folder" : "file"}
          />
          {file.name}
        </span>
        <span className="column">{formatDate(file.date)}</span>
        <span className="column">
          {file.size !== 0 ? treatSize(file.size) : "-"}
        </span>
        <span
          className="column last"
          style={{ pointerEvents: disabled ? "none" : "auto" }}
        >
          <Dropdown
            menu={isFolder ? { items: itemsFolder } : { items }}
            placement="bottomRight"
            disabled={disabled}
          >
            <MoreOutlined />
          </Dropdown>
        </span>
      </div>
    </>
  );
};

export default DockerItem;
